<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  type PropType,
  ref,
  SetupContext,
  useAttrs,
  watch,
} from "vue";
import { ControlSize, DesignVersion } from "@/models/types";

defineOptions({ name: "BcFileInput", inheritAttrs: false });

const attrs: SetupContext["attrs"] = useAttrs();

/** Specifies two-way binding for the file input */
const modelValue = defineModel<File | Array<File>>({
  validator: (value: any) =>
    value instanceof File ||
    (Array.isArray(value) && (value as any[]).every((v) => v instanceof File)),
});

const props = defineProps({
  /** Defines the file input's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables file input for upload */
  disabled: { type: Boolean, default: false },
  /** Specifies custom validation messages */
  errors: {
    type: Map as PropType<Map<string, string>>,
    default: () => new Map<string, string>(),
  },
  /** Expands the file input to the width of its parent container */
  fullWidth: { type: Boolean, default: false },
  /** Specifies a short hint (placeholder) before file upload */
  placeholder: { type: [String, Number, Boolean], default: undefined },
  /** Expands the file input to the width of the placeholder */
  placeholderWidth: { type: Boolean, default: false },
  /** Specifies that file input is read-only */
  readonly: { type: Boolean, default: false },
  /** Specifies pre-defined sizes for the file input */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
});

const emit = defineEmits<{
  (e: "change", payload: File | Array<File> | null): void;
  (e: "update:model-value", payload: File | Array<File> | null): void;
  (e: "validate", payload: File | Array<File> | null): void;
}>();

/** Specifies the file input validation message keys */
const errorKeys = ref<Array<string>>([]);
/** Specifies that the file input is filled out properly */
const validity = ref(true);

/** The file input reference */
const fileInputRef = ref<HTMLInputElement | null>(null);
/** The text input reference (for showing selected files) */
const textInputRef = ref<HTMLInputElement | null>(null);

/** Specifies two-way binding for the file input */
const files = computed({
  get: () => modelValue.value,
  set: (value) => emit("update:model-value", value!),
});
/** Computes the allowed extensions based on the `accept` attribute */
const extensions = computed<string[]>(() => {
  if (!attrs.accept) {
    return [];
  }

  return (attrs.accept as string)
    .trim()
    .replace(" ", "")
    .split(",")
    .filter((ext) => ext.startsWith("."));
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as any);
const inheritedAttrs = computed(() => {
  const { class: string, style: any, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

/**
 * Checks whether the file input has any constraints and whether it satisfies them.
 * @returns The element satisfies its constraints.
 */
const checkValidity = function (): boolean {
  errorKeys.value = [];
  validity.value = true;
  if (fileInputRef.value) fileInputRef.value.title = "";

  if (props.disabled || props.readonly) {
    validity.value = true;
    return validity.value;
  }

  if (
    attrs.required &&
    (!textInputRef.value?.value || textInputRef.value?.value === "")
  ) {
    errorKeys.value.push("valueMissing");
    if (fileInputRef.value)
      fileInputRef.value.title = getValidationMessage("valueMissing");
    validity.value = false;
  } else if (attrs.accept) {
    if (
      getFileExtensions().some(
        (ext) =>
          typeof extensions.value.find((e) => e.toLowerCase() === ext.toLowerCase()) ===
          "undefined"
      )
    ) {
      errorKeys.value.push("badInput");
      if (fileInputRef.value) fileInputRef.value.title = getValidationMessage("badInput");
      validity.value = false;
    }
  }

  if (
    fileInputRef.value &&
    fileInputRef.value.files &&
    fileInputRef.value.files.length > 0
  ) {
    !attrs.multiple
      ? emit("validate", fileInputRef.value.files.item(0))
      : emit("validate", Array.from(fileInputRef.value.files));
  } else if (modelValue.value) {
    emit("validate", modelValue.value);
  }
  return validity.value ?? false;
};

/**
 * Gets the selected file(s) extension(s).
 * @returns {string[]} file extension list
 */
function getFileExtensions(): string[] {
  let extensions: string[] = [];
  if (fileInputRef.value?.files) {
    for (let i = 0; i < fileInputRef.value.files.length; i++) {
      if (
        fileInputRef.value.files.item(i) &&
        fileInputRef.value.files.item(i)!.name.lastIndexOf(".") !== -1
      ) {
        extensions.push(
          fileInputRef.value.files
            .item(i)!
            .name.slice(fileInputRef.value.files.item(i)!.name.lastIndexOf("."))
        );
      }
    }
  } else if (modelValue.value) {
    if (
      modelValue.value instanceof File &&
      modelValue.value.name.lastIndexOf(".") !== -1
    ) {
      extensions.push(
        modelValue.value.name.slice(modelValue.value.name.lastIndexOf("."))
      );
    } else if (Array.isArray(modelValue.value)) {
      modelValue.value.forEach((file) => {
        if (file && file.name.lastIndexOf(".") !== -1) {
          extensions.push(file.name.slice(file.name.lastIndexOf(".")));
        }
      });
    }
  }
  return extensions;
}

/** Gets the selected file names. */
function getFileNames(): string {
  let names = "";
  if (fileInputRef.value?.files && fileInputRef.value?.files.length > 0) {
    for (let i = 0; i < fileInputRef.value.files.length; i++) {
      if (fileInputRef?.value.files.item(i)) {
        i < fileInputRef.value.files.length - 1
          ? (names += `${fileInputRef.value.files.item(i)!.name}, `)
          : (names += fileInputRef.value.files.item(i)!.name);
      }
    }
  } else if (modelValue.value) {
    if (modelValue.value instanceof File) {
      return modelValue.value.name;
    } else if (Array.isArray(modelValue.value)) {
      if (!attrs.multiple && modelValue.value?.length > 0) {
        return modelValue.value[0].name;
      }
      modelValue.value.forEach((file, fileIdx, fileList) => {
        if (file) {
          fileIdx < fileList.length - 1
            ? (names += `${file.name}, `)
            : (names += file.name);
        }
      });
    }
  }
  return names;
}

/**
 * Gets validation message based on error key
 * @param {string} errorKey error key
 * @param {string[]|number[]} replaceList list of strings to replace in text
 */
function getValidationMessage(
  errorKey: string,
  ...replaceList: Array<string | number>
): string {
  let error = props.errors.get(errorKey) ?? errorKey;
  replaceList.forEach((item, idx) => {
    error = error.replace(`{${idx}}`, item.toString());
  });
  return error;
}

/**
 * Updates the file input and emits change event.
 */
async function onFileChange(): Promise<void> {
  if (fileInputRef.value?.files) {
    if (!attrs.multiple) {
      modelValue.value = fileInputRef.value.files.item(0)!;
      emit("change", fileInputRef.value.files.item(0));
    } else {
      modelValue.value = Array.from(fileInputRef.value.files);
      emit("change", Array.from(fileInputRef.value.files));
    }
    await nextTick(() => {
      if (textInputRef.value) textInputRef.value.value = getFileNames();
      checkValidity();
    });
  }
  fileInputRef.value?.blur();
}

/** Watches the `errors` attribute */
function onErrorsChanged(): void {
  if (fileInputRef.value && errorKeys.value.length) {
    fileInputRef.value.title = props.errors.get(errorKeys.value[0]) ?? "";
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  if (fileInputRef.value) {
    fileInputRef.value.title =
      value && errorKeys.value.length ? "" : props.errors.get(errorKeys.value[0]) ?? "";
  }
}

onMounted(() => {
  if (textInputRef.value) {
    textInputRef.value.value = getFileNames();
    textInputRef.value.title = "";
  }
  if (fileInputRef.value) fileInputRef.value.title = "";

  nextTick(() => {
    if (!props.disabled && !props.readonly && modelValue.value) {
      checkValidity();
    }
  });
});

watch(() => props.errors, onErrorsChanged, { deep: true });
watch(() => props.disabled, onDisabledOrReadonlyChanged);
watch(() => props.readonly, onDisabledOrReadonlyChanged);

defineExpose({
  modelValue,
  files,
  errorKeys,
  validity,
  fileInputRef,
  textInputRef,
  extensions,
  checkValidity,
  getValidationMessage,
});
</script>

<template>
  <div
    :data-pak="pak"
    class="bissantzControls fileInputControl"
    :class="[
      size ? `file-input-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        invalid: !validity,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    :style="rootStyle"
    :data-design="design"
  >
    <span v-if="placeholderWidth && placeholder" class="widthtestbox">{{
      placeholder
    }}</span>
    <div class="inputContainer">
      <input
        ref="textInputRef"
        type="text"
        readonly
        tabindex="-1"
        class="textInput"
        :placeholder="placeholder?.toString()"
        :disabled="disabled"
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 10 20"
        width="0.625em"
        height="100%"
      >
        <polygon points="10,5 0,5 5,0" stroke-width="1" />
        <line x1="5" y1="30" x2="5" y2="0" stroke-width="1" />
      </svg>
      <input
        ref="fileInputRef"
        class="fileInput"
        type="file"
        v-bind="inheritedAttrs"
        :disabled="readonly || disabled"
        @change="onFileChange"
      />
    </div>
    <span v-if="!disabled && !validity && errorKeys.length" class="validationMessage">
      <span v-for="(errorKey, idx) in errorKeys" :key="idx">
        {{ getValidationMessage(errorKey) }}
      </span>
    </span>
  </div>
</template>

<style lang="less">
@import "../styles/components/bc-file-input.less";
</style>
